import { Link } from "react-router-dom";
import type { BrandEdit } from "../../../../shared/types";
import { timeAgo } from "../../lib/utils";
import { Button } from "../ui/Button";

interface CorrectionsListProps {
  edits: BrandEdit[];
  onArchive?: (correctionId: string) => void;
  archivingId?: string | null;
  readOnly?: boolean;
}

function sourceHref(edit: BrandEdit): string | null {
  const appliedRunId = edit.applied_run_ids[0];
  return appliedRunId ? `/runs/${appliedRunId}` : null;
}

export function CorrectionsList({
  edits,
  onArchive,
  archivingId,
  readOnly = false,
}: CorrectionsListProps) {
  if (edits.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-white p-6 text-sm text-slate-600">
        No corrections yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {edits.map((edit) => (
        <div key={edit.id} className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-800">
                {edit.kind === "correction_note" ? edit.correction_text : edit.human_version}
              </p>
              {edit.kind === "correction_note" && edit.trust_tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {edit.trust_tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600"
                    >
                      {tag.replaceAll("_", " ")}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                <span>{timeAgo(edit.created_at)}</span>
                {sourceHref(edit) ? (
                  <Link to={sourceHref(edit)!} className="text-blue-700 underline">
                    View applied run
                  </Link>
                ) : edit.source_id ? (
                  <span>Source: {edit.source_type}</span>
                ) : null}
              </div>
            </div>
            {!readOnly && edit.kind === "correction_note" && onArchive && (
              <Button
                size="sm"
                variant="ghost"
                disabled={archivingId === edit.id}
                onClick={() => onArchive(edit.id)}
              >
                {archivingId === edit.id ? "Archiving..." : "Archive"}
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
